/**
 * RoleBadge Component
 *
 * Displays a B2B contact role with color-coded badge and icon.
 * Features subtle gradient backgrounds and refined micro-animations.
 *
 * @example
 * <RoleBadge role="champion" />
 * <RoleBadge role="decision_maker" size="sm" />
 * <RoleBadge role="signer" removable onRemove={() => {}} />
 */

import * as React from 'react';
import {
  StarIcon,
  SparklesIcon,
  UserIcon,
  ShieldCheckIcon,
  BanknotesIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import { BuyerRoleColors, BuyerRoleMetadata, type BuyerRole } from '@revont/shared-types';

export type RoleBadgeSize = 'xs' | 'sm' | 'md';

export interface RoleBadgeProps {
  /** The buyer role to display */
  role: BuyerRole;
  /** Size variant */
  size?: RoleBadgeSize;
  /** Show remove button */
  removable?: boolean;
  /** Remove callback */
  onRemove?: () => void;
  /** Additional className */
  className?: string;
  /** Show as interactive (clickable appearance) */
  interactive?: boolean;
  /** Click handler */
  onClick?: () => void;
}

// Custom crown icon for Decision Maker
function CrownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
    </svg>
  );
}

// Role icons mapping
const roleIcons: Record<BuyerRole, React.ComponentType<{ className?: string }>> = {
  champion: StarIcon,
  decision_maker: CrownIcon,
  influencer: SparklesIcon,
  end_user: UserIcon,
  gatekeeper: ShieldCheckIcon,
  economic_buyer: BanknotesIcon,
  signer: PencilSquareIcon,
};

// Role configuration combining icons with colors from shared-types
const roleConfig: Record<
  BuyerRole,
  {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    colors: {
      bg: string;
      text: string;
      border: string;
      iconBg: string;
      gradient: string;
    };
  }
> = Object.fromEntries(
  (Object.keys(BuyerRoleColors) as BuyerRole[]).map((role) => [
    role,
    {
      label: BuyerRoleMetadata[role].label,
      icon: roleIcons[role],
      colors: {
        bg: BuyerRoleColors[role].bg,
        text: BuyerRoleColors[role].text,
        border: BuyerRoleColors[role].border,
        iconBg: BuyerRoleColors[role].iconBg,
        gradient: BuyerRoleColors[role].gradient,
      },
    },
  ])
) as typeof roleConfig;

const sizeClasses: Record<RoleBadgeSize, { badge: string; icon: string; iconWrapper: string; text: string }> = {
  xs: {
    badge: 'h-5 px-1.5 gap-1',
    icon: 'w-2.5 h-2.5',
    iconWrapper: 'w-3.5 h-3.5',
    text: 'text-[10px]',
  },
  sm: {
    badge: 'h-6 px-2 gap-1.5',
    icon: 'w-3 h-3',
    iconWrapper: 'w-4 h-4',
    text: 'text-[11px]',
  },
  md: {
    badge: 'h-7 px-2.5 gap-2',
    icon: 'w-3.5 h-3.5',
    iconWrapper: 'w-5 h-5',
    text: 'text-xs',
  },
};

export function RoleBadge({
  role,
  size = 'sm',
  removable = false,
  onRemove,
  className = '',
  interactive = false,
  onClick,
}: RoleBadgeProps) {
  const config = roleConfig[role];
  const sizes = sizeClasses[size];
  const Icon = config.icon;

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove?.();
  };

  const Component = onClick ? 'button' : 'span';

  return (
    <Component
      onClick={onClick}
      className={`
        inline-flex items-center font-medium rounded-md
        border backdrop-blur-sm
        bg-gradient-to-r ${config.colors.gradient}
        ${config.colors.border}
        ${config.colors.text}
        ${sizes.badge}
        ${interactive || onClick ? 'cursor-pointer hover:brightness-110 active:scale-[0.98]' : ''}
        transition-all duration-150
        ${className}
      `}
    >
      {/* Icon with subtle background */}
      <span
        className={`
          flex items-center justify-center rounded
          ${config.colors.iconBg}
          ${sizes.iconWrapper}
        `}
      >
        <Icon className={sizes.icon} />
      </span>

      {/* Label */}
      <span className={`font-semibold tracking-tight ${sizes.text}`}>{config.label}</span>

      {/* Remove button */}
      {removable && onRemove && (
        <button
          type="button"
          onClick={handleRemove}
          className={`
            -mr-0.5 ml-0.5 p-0.5 rounded
            opacity-60 hover:opacity-100
            hover:bg-white/10
            transition-all duration-150
          `}
        >
          <XMarkIcon className={size === 'xs' ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
        </button>
      )}
    </Component>
  );
}

// Export role config for use in other components
export { roleConfig };
export default RoleBadge;
